/**
 * Flat comment list plus the inline composer.
 *
 * Comments accept screenshots and clips through the same mechanism as a report,
 * because that is how a bug actually gets diagnosed: someone replies with a
 * picture — or a recording — of it happening to them too.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { AttachmentTray } from "@/components/kehitys/AttachmentTray";
import { MediaGallery } from "@/components/kehitys/MediaGallery";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import {
  useFeedbackAttachmentUpload,
  type UploadedAttachment,
} from "@/hooks/useFeedbackAttachmentUpload";
import type { AttachmentRefInput } from "@/hooks/useFeedbackReports";
import { CloseIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { FeedbackComment } from "@/types";

type MutationOutcome = { success: boolean; message?: string | null } | null | undefined;

interface CommentThreadProps {
  comments: FeedbackComment[];
  onAdd: (
    body: string,
    attachments: AttachmentRefInput[],
  ) => Promise<MutationOutcome>;
  onDelete: (commentId: string, version: number) => Promise<MutationOutcome>;
}

function initials(first?: string | null, last?: string | null): string {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}

export function CommentThread({ comments, onAdd, onDelete }: CommentThreadProps) {
  const [body, setBody] = useState("");
  const [shots, setShots] = useState<UploadedAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const { upload, uploading } = useFeedbackAttachmentUpload();
  const { dbUser } = useAuth();

  // INTERNAL+ and ADMIN moderate anyone's comment; everyone else only their own.
  // Mirrors the same rule in the delete_feedback_comment mutation — this only
  // decides whether to render the control, the server decides the outcome.
  const mayModerate =
    dbUser?.accessLevel === "ADMIN" || dbUser?.accessLevel === "INTERNAL_PLUS";

  const removeComment = async (comment: FeedbackComment) => {
    const result = await onDelete(comment.id, comment.version);
    if (result && !result.success) {
      toast.error(result.message ?? "Kommentin poisto epäonnistui");
    }
  };

  const addFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const uploaded = await upload(file);
        if (uploaded) setShots((current) => [...current, uploaded]);
      }
    },
    [upload],
  );

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      // Only claim the paste when the comment box has focus, so pasting into the
      // create dialog is unaffected.
      if (target?.id !== "comment-body") return;
      const files = Array.from(event.clipboardData?.items ?? [])
        .filter(
          (item) =>
            item.kind === "file" &&
            (item.type.startsWith("image/") || item.type.startsWith("video/")),
        )
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
      if (files.length === 0) return;
      event.preventDefault();
      void addFiles(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [addFiles]);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const result = await onAdd(
        body.trim(),
        shots.map((shot) => ({ id: shot.id, caption: shot.caption.trim() || null })),
      );
      if (result?.success) {
        shots.forEach((shot) => {
          if (shot.previewUrl) URL.revokeObjectURL(shot.previewUrl);
        });
        setBody("");
        setShots([]);
      } else {
        toast.error(result?.message ?? "Kommentin lähetys epäonnistui");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    // No heading: the count lives on the enclosing tab. This used to render
    // "KESKUSTELU · 3 ————", a sectioning device nothing else in the app uses.
    <section className="flex flex-col gap-4">
      {comments.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Ei vielä kommentteja. Kerro jos huomaat saman.
        </p>
      )}

      <div className="flex flex-col gap-4">
        {comments.map((comment) => {
          const isTeam =
            comment.createdBy?.accessLevel === "ADMIN" ||
            comment.createdBy?.accessLevel === "INTERNAL_PLUS";
          return (
            <div key={comment.id} className="flex gap-3">
              <span
                className={cn(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold",
                  isTeam
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {initials(comment.createdBy?.firstName, comment.createdBy?.lastName)}
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-sm font-semibold">
                    {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                  </span>
                  {isTeam && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                      Tencon-tiimi
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {new Date(comment.createdAt).toLocaleDateString("fi-FI")}
                  </span>
                  {(mayModerate || comment.createdById === dbUser?.id) && (
                    <button
                      type="button"
                      onClick={() => void removeComment(comment)}
                      aria-label="Poista kommentti"
                      title="Poista kommentti"
                      className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <CloseIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <p className="max-w-prose whitespace-pre-wrap text-sm">
                  {comment.body}
                </p>
                {comment.attachments.length > 0 && (
                  <div className="pt-1">
                    <MediaGallery attachments={comment.attachments} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 p-3">
        <Textarea
          id="comment-body"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={3}
          placeholder="Kirjoita kommentti… voit liittää kuvakaappauksen suoraan Ctrl + V"
          aria-label="Kirjoita kommentti"
        />
        <AttachmentTray
          items={shots}
          onCaptionChange={(id, caption) =>
            setShots((current) =>
              current.map((shot) => (shot.id === id ? { ...shot, caption } : shot)),
            )
          }
          onRemove={(id) =>
            setShots((current) => {
              const removed = current.find((shot) => shot.id === id);
              // previewUrl is null for a video with no poster frame.
              if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
              return current.filter((shot) => shot.id !== id);
            })
          }
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={submit}
            disabled={sending || uploading || !body.trim()}
          >
            Lähetä kommentti
          </Button>
          <span className="text-[11px] text-muted-foreground">
            Ctrl + V liittää kuvakaappauksen
          </span>
        </div>
      </div>
    </section>
  );
}
